import {useMembersTranslation} from '@/i18n/hooks';
import {useTranslation} from 'react-i18next';
import {Controller, useForm} from "react-hook-form";
import {Heading, Subheading} from "@/components/tailwind/heading.tsx";
import {Text} from '@/components/tailwind/text'
import {FieldLabel} from "@/components/field-label.tsx";
import {Input} from "@/components/tailwind/input.tsx";
import {v4 as uuid} from "uuid";
import {useEffect, useState} from "react";
import {CreateMemberDTO, MemberAddress, MemberFormOptions, PhoneNumber} from "@/models/member.ts";
import {createNewMember, getMemberFormOptions} from "@/services/member-services.ts";
import {MobilityOption} from "@/models/mobility-option.ts";
import {fetchAllMobilityOptions} from "@/services/mobility-option-services.ts";
import {DistrictSelector, useDistrictSuggestion} from "@/components/district-selector.tsx";
import {StreetAutocomplete} from "@/components/street-autocomplete.tsx";
import {MobilityOptionCombobox} from "@/components/mobility-option-combobox.tsx";
import {useAuth} from "@/context/use-auth.tsx";
import ValidationAlert from "@/components/validation-alert.tsx";
import {useNavigate} from "react-router-dom";
import {ProblemDetail} from "@/types/problem-details.ts";
import {NullableTextInput} from "@/components/nullable-text-input.tsx";
import {sanitizeText} from "@/helpers/sanitizer.ts";
import {Select} from "@/components/tailwind/select.tsx";
import {Textarea} from "@/components/tailwind/textarea.tsx";
import ServiceAssignmentPanel from '@/components/service-assignment-panel';
import {ServiceGroup, ServiceWithGroupId} from "@/models/service.ts";
import {getListOfServiceGroups, getListOfServices} from "@/services/service-services.ts";
import {z} from 'zod';
import {isValidIBAN} from "ibantools-germany";
import {isValidBIC} from "bankdata-germany";
import {zodResolver} from "@hookform/resolvers/zod";
import {formatIBAN, unformatIBAN} from "@/helpers/iban-formatter.ts";
import {Button} from "@/components/tailwind/button.tsx";
import {getMembershipFeeTypes, assignFeeTypeToMember} from "@/services/membership-fee-services.ts";
import {MembershipFeeType} from "@/models/membership-fee.ts";
import {isGermanPhoneNumber} from "@/helpers/phone-helpers.ts";
import {getPrefillMemberAddressFromOrg} from "@/services/member-prefill-settings-services.ts";
import {getOrganizationDetails} from "@/services/organization-details-services.ts";
import log from '@/lib/logger'


const memberSchema = z.object({
    id: z.string().uuid(),
    firstName: z.string().min(1, {message: "memberForm.errors.first-name-required"}),
    lastName: z.string().min(1, {message: "memberForm.errors.last-name-required"}),
    companyName: z.string().optional(),
    salutation: z.object({
        id: z.string(),
        value: z.string()
    }).optional(),
    title: z.string().optional(),
    email: z.string()
        .trim()
        .transform(val => val === "" ? undefined : val)
        .refine(val => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), {
            message: "memberForm.errors.invalid-email-address"
        })
        .optional(),
    dateOfBirth: z.string({error: "memberForm.errors.birthday-required"})
        .refine(val => !val || !isNaN(Date.parse(val)), {
            message: "memberForm.errors.invalid-birthday-date-format"
        })
        .refine(val => {
            if (!val) return true;
            return val < new Date().toISOString().split('T')[0];
        }, {
            message: "memberForm.errors.birthday-needs-to-be-before-today"
        }),
    fastestMobilityOption: z.object({
        id: z.string(),
        name: z.string()
    }).optional(),
    address: z.object({
        street: z.string().min(1, {message: "memberForm.errors.address-street-required"}),
        houseNo: z.string().optional(),
        postcode: z.string()
            .regex(/^\d{5}$/, {message: "memberForm.errors.address-postcode-invalid-format"})
            .min(1, {message: "memberForm.errors.address-postcode-required"}),
        city: z.string().min(1, {message: "memberForm.errors.address-city-required"}),
    }),
    phoneNumbers: z.object({
        landline: z.string()
            .transform(val => val.trim() === "" ? undefined : val)
            .refine(val => !val || isGermanPhoneNumber(val), {
                message: "memberForm.errors.invalid-landline"
            })
            .optional(),
        mobile: z.string()
            .transform(val => val.trim() === "" ? undefined : val)
            .refine(val => !val || isGermanPhoneNumber(val), {
                message: "memberForm.errors.invalid-mobile"
            })
            .optional(),
    }).refine(
        data => !!data.landline || !!data.mobile,
        {
            message: "memberForm.errors.at-least-one-phone-number-required",
            path: ["root"]
        }
    ),
    membershipStartDate: z.string()
        .refine(val => !val || !isNaN(Date.parse(val)), {
            message: "memberForm.errors.invalid-membership-start-date-format"
        }),
    membershipStatus: z.object({
        id: z.string().min(1, {message: "memberForm.errors.membership-status-required"}),
        value: z.string()
    }),
    bankingDetails: z.object({
        accountOwner: z.string().optional(),
        iban: z.string()
            .transform(val => val.trim() === "" ? undefined : val)
            .refine(
                val => !val || isValidIBAN(val.replace(/\s+/g, '')),
                {message: "memberForm.errors.invalid-iban"}
            )
            .optional(),
        bic: z.string()
            .transform(val => val.trim() === "" ? undefined : val)
            .refine(
                val => !val || isValidBIC(val),
                {message: "memberForm.errors.invalid-bic"}
            )
            .optional(),
        bankName: z.string().optional(),
        mandateDate: z.string().optional(),
    }).optional(),
    generalComments: z.string().optional(),
    availabilityComments: z.string().optional(),
    currentNotes: z.string().optional(),
    services: z.array(z.string()).optional(),
    membershipFeeTypeId: z.string().optional(),
    isDriver: z.boolean(),
    // Donation fields
    oneTimeDonationAmount: z.string()
        .transform(val => val?.trim() === "" ? undefined : val)
        .refine(val => {
            if (!val) return true;
            const amount = parseFloat(val.replace(',', '.'));
            return !isNaN(amount) && amount > 0;
        }, { message: "memberForm.errors.donation-amount-positive" })
        .optional(),
    oneTimeDonationDate: z.string().optional(),
    annualDonationAmount: z.string()
        .transform(val => val?.trim() === "" ? undefined : val)
        .refine(val => {
            if (!val) return true;
            const amount = parseFloat(val.replace(',', '.'));
            return !isNaN(amount) && amount > 0;
        }, { message: "memberForm.errors.donation-amount-positive" })
        .optional(),
    annualDonationValidFrom: z.string().optional(),
}).refine(
    data => {
        if (data.dateOfBirth && data.membershipStartDate) {
            return data.membershipStartDate >= data.dateOfBirth;
        }
        return true;
    },
    {
        message: "memberForm.errors.membership-must-start-after-birth",
        path: ["membershipStartDate"]
    }
).refine(
    data => {
        const statusValue = data.membershipStatus?.value?.toLowerCase();
        const isActive = statusValue === 'common.status.active' || statusValue === 'aktiv';
        if (isActive && (!data.services || data.services.length === 0)) {
            return false;
        }
        return true;
    },
    {
        message: "memberForm.errors.active-member-requires-services",
        path: ["services"]
    }
);

type MemberFormValues = z.infer<typeof memberSchema>;


// TODO REFACTOR! (https://github.com/steinhauer-software/die-nachbarschaftshilfe-app/issues/66)
function mapPhoneTypeToEnum(ident: string): "MOBILE" | "LANDLINE" {
    switch (ident.toLowerCase()) {
        case "mobile":
            return "MOBILE"
        default:
            return "LANDLINE"
    }
}

export default function NewMemberPage() {

    const {t} = useMembersTranslation()
    const {t: tCommon} = useTranslation('common')
    const {token} = useAuth()
    const navigate = useNavigate()

    const todaysDateString = new Date().toISOString().split('T')[0]
    const {
        register,
        handleSubmit,
        setError,
        control,
        watch,
        setValue,
        getValues,
        formState: {errors}
    } = useForm<MemberFormValues>({
        resolver: zodResolver(memberSchema),
        defaultValues: {
            id: uuid(),
            membershipStartDate: todaysDateString,
            isDriver: false,
            bankingDetails: {
                mandateDate: todaysDateString
            },
            oneTimeDonationDate: todaysDateString,
            annualDonationValidFrom: todaysDateString,
        }
    })

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
    const [availableServiceGroups, setAvailableServiceGroups] = useState<ServiceGroup[]>([]);
    const [availableServices, setAvailableServices] = useState<ServiceWithGroupId[]>([]);
    const [membershipFeeTypes, setMembershipFeeTypes] = useState<MembershipFeeType[]>([]);
    const [noFeesWarning, setNoFeesWarning] = useState(false);
    const [mobilityOptions, setMobilityOptions] = useState<MobilityOption[]>([]);
    const {
        selectedDistrictId,
        handleDistrictChange,
        handleSuggestionSelect,
    } = useDistrictSuggestion();

    // Watch the membership status to show/hide services
    const watchedMembershipStatus = watch("membershipStatus");
    const watchedIsDriver = watch("isDriver");
    
    // Check if member is active (for conditional field display)
    const isActiveMember = watchedMembershipStatus?.value?.toLowerCase() === 'common.status.active'
        || watchedMembershipStatus?.value?.toLowerCase() === 'aktiv';

    // Clear services and driver flag when switching to Passiv
    useEffect(() => {
        if (!isActiveMember) {
            setSelectedServiceIds([]);
            setValue('services', [], { shouldValidate: false });
            setValue('isDriver', false);
        }
    }, [isActiveMember, setValue]);

    const [memberFormOptions, setMemberFormOptions] = useState<MemberFormOptions>()

    function mapPhoneNumbers(phoneNumbers: MemberFormValues["phoneNumbers"]): PhoneNumber[] {
        const phoneTypes = ["landline", "mobile"] as const;
        return phoneTypes
            .filter(type => phoneNumbers?.[type])
            .map(type => ({
                id: uuid(),
                type: mapPhoneTypeToEnum(type),
                number: phoneNumbers![type]!
            }));
    }

    function mapAddress(address: MemberFormValues["address"]): MemberAddress | undefined {
        if (address && Object.keys(address).length === 4 && Object.values(address).every(Boolean)) {
            return {
                id: uuid(),
                street: address.street!,
                houseNumber: address.houseNo!,
                postcode: address.postcode!,
                city: address.city!,
                districtId: selectedDistrictId || undefined,
            };
        }
        return undefined;
    }

    function mapBankingDetails(formValue: MemberFormValues) {
        if (!formValue.bankingDetails || !formValue.bankingDetails.iban) {
            return undefined
        }

        return {
            accountOwner: formValue.bankingDetails.accountOwner,
            iban: formValue.bankingDetails.iban,
            bic: formValue.bankingDetails.bic,
            bankName: formValue.bankingDetails.bankName,
            mandateDate: formValue.bankingDetails.mandateDate!
        };
    }

    async function handleSubmission(formValue: MemberFormValues) {
        const memberAddr = mapAddress(formValue.address);
        const phoneNumbers = mapPhoneNumbers(formValue.phoneNumbers);

        // Map the membership status value to the MemberStatus enum
        const statusValue = formValue.membershipStatus.value.toLowerCase();
        const memberStatus: 'ACTIVE_MEMBER' | 'PASSIVE_MEMBER' = 
            (statusValue === 'common.status.active' || statusValue === 'aktiv') 
                ? 'ACTIVE_MEMBER' 
                : 'PASSIVE_MEMBER';

        const memberDto: CreateMemberDTO = {
            id: formValue.id,
            companyName: formValue.companyName,
            firstName: formValue.firstName,
            lastName: formValue.lastName,
            salutationId: formValue.salutation?.id,
            title: formValue.title,
            address: memberAddr,
            dateOfBirth: formValue.dateOfBirth,
            membershipStartDate: formValue.membershipStartDate,
            phoneNumbers: phoneNumbers,
            membershipStatusId: formValue.membershipStatus.id,
            memberStatus: memberStatus,
            generalComments: sanitizeText(formValue.generalComments),
            availabilityComments: sanitizeText(formValue.availabilityComments),
            currentNotes: sanitizeText(formValue.currentNotes),
            email: formValue.email,
            bankingDetails: mapBankingDetails(formValue),
            services: selectedServiceIds,
            isDriver: formValue.isDriver,
            fastestMobilityOptionId: formValue.fastestMobilityOption?.id,
            oneTimeDonation: formValue.oneTimeDonationAmount ? {
                amount: parseFloat(formValue.oneTimeDonationAmount.replace(',', '.')),
                donationDate: formValue.oneTimeDonationDate || todaysDateString
            } : undefined,
            annualDonation: formValue.annualDonationAmount ? {
                amount: parseFloat(formValue.annualDonationAmount.replace(',', '.')),
                validFrom: formValue.annualDonationValidFrom || todaysDateString
            } : undefined,
        };

        const response = createNewMember(token!, memberDto);

        response
            .then(async (createdMember) => {
                // Assign fee type if selected
                if (formValue.membershipFeeTypeId && createdMember?.id) {
                    try {
                        await assignFeeTypeToMember(token!, createdMember.id, {
                            feeTypeId: formValue.membershipFeeTypeId,
                            validFrom: formValue.membershipStartDate
                        });
                    } catch (error) {
                        log.error("Failed to assign fee type:", error);
                    }
                }
                if (createdMember?.id) {
                    // Navigate to the new member's detail page and signal a one-time
                    // post-creation banner so office staff can read the assigned number.
                    navigate(`/members/${createdMember.id}`, {state: {justCreated: true}});
                } else {
                    navigate("/members");
                }
            })
            .catch((reason: ProblemDetail) => {
                log.warn("An error occurred while performing create new member request", reason);

                if (reason.validationIssues) {
                    reason.validationIssues.forEach(
                        finding => {
                            setError(
                                finding.field as keyof MemberFormValues,
                                {
                                    message: finding.message
                                }
                            );
                        }
                    );
                    
                    // Show detailed validation errors
                    const errorDetails = reason.validationIssues.map(v => v.message).join(', ');
                    setError(
                        "root.serverError",
                        {
                            type: `${reason.status}`,
                            message: errorDetails || tCommon("serverError.responseCode", {statusCode: reason.status})
                        }
                    );
                } else {
                    // Show generic or specific error message
                    const errorMessage = reason.detail || reason.title || 
                        tCommon("serverError.responseCode", {statusCode: reason.status});
                    setError(
                        "root.serverError",
                        {
                            type: `${reason.status}`,
                            message: errorMessage
                        }
                    );
                }
            });
    }

    useEffect(() => {
        const fetchOptions = getMemberFormOptions(token!)

        fetchOptions
            .then((options) => {
                if (options !== undefined) {
                    setMemberFormOptions(options)
                    // Set default membership status to Passiv
                    if (options.supportedStatus && options.supportedStatus.length > 0) {
                        const passivStatus = options.supportedStatus.find(
                            s => s.value.toLowerCase() === 'passiv'
                        );
                        setValue('membershipStatus', passivStatus ?? options.supportedStatus[0])
                    }
                    setIsLoading(false)
                }

            })
            .catch((error) => {
                    log.error("Couldn't load options from the backend. Here is what I know: ", error)
                }
            )

        const serviceGroups = getListOfServiceGroups(token!)
        serviceGroups.then((foundGroups: void | ServiceGroup[]) => {
                if (foundGroups)
                    setAvailableServiceGroups(foundGroups);
            }
        )

        const services = getListOfServices(token!)
        services.then((foundServices) => {
            if (foundServices)
                setAvailableServices(foundServices)
        })

        // Load membership fee types
        getMembershipFeeTypes(token!)
            .then((feeTypes) => {
                setMembershipFeeTypes(feeTypes || []);
                if (!feeTypes || feeTypes.length === 0) {
                    setNoFeesWarning(true);
                } else {
                    // Set default fee type if available
                    const defaultFeeType = feeTypes.find(ft => ft.isDefault);
                    if (defaultFeeType) {
                        setValue('membershipFeeTypeId', defaultFeeType.id);
                    }
                }
            })
            .catch(error => {
                log.error("Failed to load membership fee types:", error);
                setNoFeesWarning(true);
            });

        // Load mobility options
        fetchAllMobilityOptions(token!)
            .then((options) => {
                if (options) {
                    setMobilityOptions(options);
                }
            })
            .catch((error) => {
                log.error("Failed to load mobility options:", error);
            });

        // Pre-fill postal code and city from the organization address when enabled.
        // Only request the organization address when the toggle is on, and never
        // overwrite a value the user has already entered.
        getPrefillMemberAddressFromOrg(token!)
            .then((prefillEnabled) => {
                if (!prefillEnabled) return;
                return getOrganizationDetails(token!).then((orgDetails) => {
                    if (!orgDetails || !orgDetails.postalCode || !orgDetails.city) return;
                    if (!getValues("address.postcode")) {
                        setValue("address.postcode", orgDetails.postalCode);
                    }
                    if (!getValues("address.city")) {
                        setValue("address.city", orgDetails.city);
                    }
                });
            })
            .catch((error) => {
                log.error("Failed to load member address pre-fill settings:", error);
            });

    }, [token, setValue, getValues])

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Heading>{t('memberForm.loadingMessage')}</Heading>
            </div>
        )
    }

    return (
        <form onSubmit={handleSubmit(handleSubmission)}>
            <Input
                id="id"
                type="hidden"
                {...register("id")}
            />

            <Heading>{t('memberForm.mainHeader.new')}</Heading>

            {errors?.root?.serverError && (
                <ValidationAlert
                    message={errors?.root?.serverError?.message || t("common.errors.unknownServerError")}/>
            )}

            <div className="mt-10 ml-5 space-y-12">

                <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div>
                        <Subheading className="text-base/7 font-semibold">{t('memberForm.sections.personalInfo.heading')}</Subheading>
                        <Text className="mt-1 text-sm/6">
                            {t('memberForm.sections.personalInfo.text')}
                        </Text>
                    </div>

                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">

                        <div className={"sm:col-span-4 grid"}>
                            <div className={"flex items-center space-x-4"}>
                                <Text className={"text-sm/6 font-medium"}>{t('memberForm.fields.memberNo.label')}:</Text>
                                <Text className={"text-sm/6 font-normal"}>{t('memberForm.fields.memberNo.info')}</Text>
                            </div>
                        </div>

                        <div className={"sm:col-span-3"}>
                            <FieldLabel htmlFor={"salutation"}>
                                <Text>{t('memberForm.fields.salutation.label')}</Text>
                            </FieldLabel>
                            <div className={"mt-2"}>
                                <Controller
                                    name="salutation"
                                    control={control}
                                    render={({field: {onChange, value, ...field}}) => (
                                        <Select
                                            id="salutation"
                                            {...field}
                                            value={value?.id ?? "__NULL__"}
                                            onChange={(e) => {
                                                const selectedId = e.target.value;
                                                onChange(
                                                    selectedId === "__NULL__"
                                                        ? null
                                                        : memberFormOptions?.salutations?.find(s => s.id === selectedId)
                                                );
                                            }}
                                            data-testid="member-salutation"
                                        >
                                            <option value="__NULL__">--</option>
                                            {memberFormOptions?.salutations?.map((s) => (
                                                <option key={s.id} value={s.id}>
                                                    {t(s.value)}
                                                </option>
                                            ))}
                                        </Select>
                                    )}
                                />
                                {errors.salutation && (
                                    <ValidationAlert
                                        message={t(errors.salutation?.message || "memberForm.errors.unknown-salutation-issue")}/>                                )}
                            </div>
                        </div>

                        <div className={"sm:col-span-3"}>
                            <FieldLabel htmlFor={"title"}>
                                <Text>{t('memberForm.fields.title.label')}</Text>
                            </FieldLabel>
                            <div className={"mt-2"}>
                                <NullableTextInput
                                    id={"title"}
                                    name={"title"}
                                    control={control}
                                    autoComplete="honorific-prefix"
                                    aria-label={t("memberForm.fields.title.label" as any)}
                                    data-invalid={!!errors.title || null}
                                    placeholder={t("memberForm.fields.title.placeholder" as any)}
                                    data-testid="member-title"
                                />

                                {errors.title && (
                                    <ValidationAlert
                                        message={t(errors.title?.message || "memberForm.errors.unknown-title-issue")}/>
                                )}
                            </div>
                        </div>

                        <div className="sm:col-span-3">
                            <FieldLabel htmlFor="first-name">
                                <Text>{t('memberForm.fields.firstName.label')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Input
                                    id="first-name"
                                    {...register("firstName")}
                                    autoComplete="given-name"
                                    type="text"
                                    aria-label={t("memberForm.fields.firstName.label" as any)}
                                    data-invalid={!!errors.firstName || null}
                                    placeholder={t("memberForm.fields.firstName.placeholder" as any)}
                                    data-testid="member-firstname"
                                />
                                {errors.firstName && (
                                    <ValidationAlert
                                        message={t(errors.firstName?.message || "memberForm.errors.unknown-firstName-issue")}/>
                                )}

                            </div>
                        </div>

                        <div className="sm:col-span-3">
                            <FieldLabel htmlFor="last-name">
                                <Text>{t('memberForm.fields.lastName.label')} </Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Input
                                    id="last-name"
                                    {...register("lastName")}
                                    type="text"
                                    autoComplete="family-name"
                                    aria-label={t("memberForm.fields.lastName.label" as any)}
                                    data-invalid={!!errors.lastName || null}
                                    placeholder={t("memberForm.fields.lastName.placeholder" as any)}
                                    data-testid="member-lastname"
                                />
                                {errors.lastName && (
                                    <ValidationAlert
                                        message={t(errors.lastName?.message || "memberForm.errors.unknown-lastName-issue")}/>
                                )}

                            </div>
                        </div>

                        <div className="sm:col-span-full">
                            <FieldLabel htmlFor="company-name">
                                <Text>{t('memberForm.fields.companyName.label')} </Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Input
                                    id="company-name"
                                    {...register("companyName")}
                                    type="text"
                                    autoComplete="organization"
                                    aria-label={t("memberForm.fields.companyName.label" as any)}
                                    data-invalid={!!errors.companyName || null}
                                    placeholder={t("memberForm.fields.companyName.placeholder" as any)}
                                />
                                {errors.companyName && (
                                    <ValidationAlert
                                        message={t(errors.companyName?.message || "memberForm.errors.unknown-company-name-issue")}/>
                                )}
                            </div>
                        </div>

                        <div className="sm:col-span-4">
                            <FieldLabel htmlFor="date-of-birth">
                                <Text>{t('memberForm.fields.dateOfBirth.label')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Controller
                                    name="dateOfBirth"
                                    control={control}
                                    render={({field}) => (
                                        <Input
                                            id="date-of-birth"
                                            type="date"
                                            autoComplete="bday"
                                            max={new Date().toISOString().split('T')[0]}
                                            onChange={(e) => {
                                                const value = e.target.value || null;
                                                field.onChange(value);
                                            }}
                                            aria-label={t("memberForm.fields.dateOfBirth.label" as any)}
                                            data-invalid={!!errors.dateOfBirth || null}
                                            data-testid="member-birthdate"
                                        />
                                    )}
                                />

                                {errors.dateOfBirth && (
                                    <ValidationAlert
                                        message={t(errors.dateOfBirth?.message || "memberForm.errors.unknown-birthday-issue")}/>
                                )}

                            </div>
                        </div>

                    </div>
                </div>

                <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div>
                        <Subheading className="text-base/7 font-semibold">
                            {t('memberForm.sections.addresses.heading')}
                        </Subheading>
                        <Text className="mt-1 text-sm/6">
                            {t('memberForm.sections.addresses.text')}
                        </Text>

                        {errors.address &&
                            <ValidationAlert
                                message={t(errors.address?.root?.message ||
                                    "memberForm.errors.unknown-address-details-error")}/>
                        }
                    </div>

                    <div data-invalid={!!errors.address}
                         className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2"
                    >
                        {errors.address && (
                            <div className="sm:col-span-5 sm:col-start-1">
                                {[
                                    errors.address?.street,
                                    errors.address?.houseNo,
                                    errors.address?.postcode,
                                    errors.address?.city,
                                ].map((error, index) => (
                                    <div key={index} className="min-h-[40px]">
                                        {error && (
                                            <ValidationAlert
                                                message={t(
                                                    error?.message ||
                                                    "memberForm.errors.unknown-address-error"
                                                )}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="sm:col-span-4 sm:col-start-1">
                            <FieldLabel htmlFor="street">
                                <Text>{t('memberForm.fields.address.street.label')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Controller
                                    name="address.street"
                                    control={control}
                                    render={({field}) => (
                                        <StreetAutocomplete
                                            id="street"
                                            value={field.value || ''}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            onSuggestionSelect={(suggestion) => {
                                                field.onChange(suggestion.street);
                                                handleSuggestionSelect(suggestion.districtId);
                                            }}
                                            aria-label={t("memberForm.fields.address.street.label" as any)}
                                            invalid={!!errors.address?.street}
                                            placeholder={t("memberForm.fields.address.street.placeholder" as any)}
                                            autoComplete="street-address"
                                            data-testid="member-street"
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        <div className="col-span-1">
                            <FieldLabel htmlFor="house-no">
                                <Text>{t('memberForm.fields.address.houseNo.label')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Input
                                    id="house-no"
                                    {...register("address.houseNo")}
                                    type="text"
                                    aria-label={t("memberForm.fields.address.houseNo.label" as any)}
                                    data-invalid={!!errors.address?.houseNo || null}
                                    placeholder={t("memberForm.fields.address.houseNo.placeholder" as any)}
                                    data-testid="member-housenumber"
                                />

                            </div>
                        </div>

                        <div className="sm:col-span-1 sm:col-start-1">
                            <FieldLabel htmlFor="postcode">
                                <Text>{t('memberForm.fields.address.postcode.label')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Input
                                    id="postcode"
                                    {...register("address.postcode")}
                                    type="text"
                                    aria-label={t("memberForm.fields.address.postcode.label" as any)}
                                    data-invalid={!!errors.address?.postcode || null}
                                    placeholder={t("memberForm.fields.address.postcode.placeholder" as any)}
                                    autoComplete="postal-code"
                                    data-testid="member-postcode"
                                />
                            </div>
                        </div>

                        <div className="sm:col-span-3">
                            <FieldLabel htmlFor="city">
                                <Text>{t('memberForm.fields.address.city.label')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Input
                                    id="city"
                                    {...register("address.city")}
                                    type="text"
                                    aria-label={t("memberForm.fields.address.city.label" as any)}
                                    data-invalid={!!errors.address?.city || null}
                                    placeholder={t("memberForm.fields.address.city.placeholder" as any)}
                                    autoComplete="locality"
                                    data-testid="member-city"
                                />
                            </div>
                        </div>

                        <div className="sm:col-span-3">
                            <FieldLabel htmlFor="district">
                                <Text>{t('memberForm.fields.address.district.label')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <DistrictSelector
                                    id="district"
                                    value={selectedDistrictId}
                                    onChange={handleDistrictChange}
                                    placeholder={t('memberForm.fields.address.district.placeholder')}
                                    aria-label={t("memberForm.fields.address.district.label" as any)}
                                    data-testid="member-district"
                                />
                            </div>
                        </div>
                    </div>
                </div>


                <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div>
                        <Subheading className="text-base/7 font-semibold">
                            {t('memberForm.sections.contact.heading')}
                        </Subheading>
                        <Text className="mt-1 text-sm/6">
                            {t('memberForm.sections.contact.text')}
                        </Text>

                        {errors?.phoneNumbers && (
                            <ValidationAlert
                                message={t(errors?.phoneNumbers?.message || errors?.phoneNumbers?.root?.message ||
                                    "memberForm.errors.invalid-phone-numbers")}/>
                        )}

                    </div>

                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">

                        <div className="sm:col-span-4">
                            <FieldLabel htmlFor="email">
                                <Text>{t('memberForm.fields.email.label')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Input
                                    id="email"
                                    {...register("email")}
                                    type="email"
                                    autoComplete="email"
                                    aria-label={t("memberForm.fields.email.label" as any)}
                                    data-invalid={!!errors.email || null}
                                    placeholder={t("memberForm.fields.email.placeholder" as any)}
                                    data-testid="member-email"
                                />
                                {errors.email &&
                                    <ValidationAlert
                                        message={t(errors.email?.message || "memberForm.errors.unknown-email-error")}/>}

                            </div>
                        </div>

                        <div className="sm:col-span-4 sm:col-start-1">
                            <FieldLabel htmlFor="phone-landline">
                                <Text>{t('memberForm.fields.phone.landline.label')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Input
                                    id="phone-landline"
                                    {...register("phoneNumbers.landline")}
                                    type="tel"
                                    aria-label={t("memberForm.fields.phone.landline.label" as any)}
                                    data-invalid={!!errors.phoneNumbers?.landline || !!errors?.phoneNumbers || null}
                                    placeholder={t("memberForm.fields.phone.landline.placeholder" as any)}
                                    autoComplete="home tel"
                                    data-testid="member-phone"
                                />
                                {errors.phoneNumbers?.landline &&
                                    <ValidationAlert
                                        message={t(errors.phoneNumbers?.landline?.message ||
                                            "memberForm.errors.unknown-phone-landline-error")}/>}

                            </div>
                        </div>

                        <div className="sm:col-span-4 sm:col-start-1">
                            <FieldLabel htmlFor="phone-mobile">
                                <Text>{t('memberForm.fields.phone.mobile.label')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Input
                                    id="phone-mobile"
                                    {...register("phoneNumbers.mobile")}
                                    type="tel"
                                    aria-label={t("memberForm.fields.phone.mobile.label" as any)}
                                    data-invalid={!!errors.phoneNumbers?.mobile || !!errors?.phoneNumbers || null}
                                    placeholder={t("memberForm.fields.phone.mobile.placeholder" as any)}
                                    autoComplete="home tel"
                                />
                                {errors.phoneNumbers?.mobile &&
                                    <ValidationAlert
                                        message={t(errors.phoneNumbers?.mobile?.message ||
                                            "memberForm.errors.unknown-phone-mobile-error")}/>}

                            </div>
                        </div>


                    </div>
                </div>

                <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div>
                        <Subheading className="text-base/7 font-semibold">
                            {t('memberForm.sections.membership.heading')}
                        </Subheading>
                        <Text className="mt-1 text-sm/6">
                            {t('memberForm.sections.membership.text')}
                        </Text>
                    </div>

                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">

                        <div className="sm:col-span-4">
                            <FieldLabel htmlFor="membership-start-date">
                                <Text>{t('memberForm.fields.membershipStart.label')}</Text>
                            </FieldLabel>
                            <div className="mt-2">

                                <Controller
                                    name="membershipStartDate"
                                    control={control}
                                    render={({field}) => (
                                        <Input
                                            id="membership-start-date"
                                            type="date"
                                            value={field.value || ''}
                                            onChange={(e) => {
                                                const value = e.target.value || '';
                                                field.onChange(value);
                                            }}
                                            aria-label={t("memberForm.fields.membershipStart.label" as any)}
                                            data-invalid={!!errors.membershipStartDate || null}
                                            data-testid="member-start-date"
                                        />
                                    )}
                                />

                                {errors.membershipStartDate &&
                                    <ValidationAlert
                                        message={t(errors.membershipStartDate?.message ||
                                            "memberForm.errors.unknown-membership-start-error")}/>
                                }

                            </div>
                            <div className="mt-2">
                                <FieldLabel htmlFor="membership-status">
                                    <Text>{t('memberForm.fields.membershipStatus.label')}</Text>
                                </FieldLabel>
                                <div className={"mt-2"}>
                                    <Controller
                                        name="membershipStatus"
                                        control={control}
                                        rules={{required: true}}
                                        render={({field}) => (
                                            <Select
                                                id="membership-status"
                                                value={field.value?.id || ''}
                                                onChange={e => {
                                                    const id = e.target.value;
                                                    const status = memberFormOptions?.supportedStatus?.find(s => s.id === id);
                                                    if (status) {
                                                        field.onChange(status);
                                                    }
                                                }}
                                                data-testid="member-type"
                                            >
                                                {memberFormOptions?.supportedStatus?.map(item => (
                                                    <option key={item.id} value={item.id}>
                                                        {item.value}
                                                    </option>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.membershipStatus &&
                                        <ValidationAlert
                                            message={t(errors.membershipStatus?.message || "memberForm.errors.unknown-membership-status-error")}/>}

                                </div>
                            </div>
                            
                            {/* Membership Fee Type Selection */}
                            <div className="sm:col-span-4 mt-4">
                                <FieldLabel htmlFor="membership-fee-type">
                                    <Text>{t('memberForm.fields.membershipFeeType.label')}</Text>
                                </FieldLabel>
                                <div className="mt-2">
                                    {noFeesWarning ? (
                                        <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4">
                                            <div className="flex">
                                                <div className="ml-3">
                                                    <p className="text-sm text-blue-700 dark:text-blue-300">
                                                        {t('memberForm.warnings.noMembershipFeesConfigured')}
                                                    </p>
                                                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                                        {t('memberForm.warnings.configureMembershipFeesHint')}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <Controller
                                            name="membershipFeeTypeId"
                                            control={control}
                                            render={({field}) => (
                                                <Select
                                                    id="membership-fee-type"
                                                    value={field.value || ''}
                                                    onChange={field.onChange}
                                                    data-testid="member-fee-type"
                                                >
                                                    {membershipFeeTypes.map(feeType => (
                                                        <option key={feeType.id} value={feeType.id}>
                                                            {feeType.name} {feeType.currentAmount !== undefined && (
                                                                feeType.currentAmount === 0 ? '(beitragsfrei)' : `(${feeType.currentAmount.toFixed(2)} €/Jahr)`
                                                            )}
                                                        </option>
                                                    ))}
                                                </Select>
                                            )}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                {isActiveMember && (
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                        <div>
                            <Subheading className="text-base/7 font-semibold">
                                {t('memberForm.sections.services.heading')}
                            </Subheading>
                            <Text className="mt-1 text-sm/6">
                                {t('memberForm.sections.services.text')}
                            </Text>
                        </div>

                        <div className="max-w-2xl md:col-span-2">
                            <ServiceAssignmentPanel
                                services={availableServices}
                                groups={availableServiceGroups}
                                selectedServiceIds={selectedServiceIds}
                                onServiceChange={(ids: string[]) => {
                                    setSelectedServiceIds(ids);
                                    setValue('services', ids, { shouldValidate: true });
                                }}
                                isDriver={watchedIsDriver}
                                onDriverChange={(value: boolean) => {
                                    setValue('isDriver', value);
                                }}
                            />

                            {errors.services && (
                                <ValidationAlert
                                    message={t(errors.services?.message || "memberForm.errors.unknown-services-error")}/>
                            )}
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div>
                        <Subheading className="text-base/7 font-semibold">
                            {t('memberForm.sections.banking.heading')}
                        </Subheading>
                        <Text className="mt-1 text-sm/6">
                            {t('memberForm.sections.banking.text')}
                        </Text>
                    </div>


                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">

                        <div className="sm:col-span-4">
                            <FieldLabel htmlFor="account-owner">
                                <Text>{t('memberForm.fields.banking.accountOwner.label')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Input
                                    id="account-owner"
                                    {...register("bankingDetails.accountOwner")}
                                    type="text"
                                    autoComplete="name"
                                    aria-label={t("memberForm.fields.banking.accountOwner.ariaLabel" as any)}
                                    data-invalid={!!errors.bankingDetails?.accountOwner || null}
                                    placeholder={t("memberForm.fields.banking.accountOwner.placeholder" as any)}
                                    data-testid="member-account-owner"
                                />
                                {errors.bankingDetails?.accountOwner &&
                                    <ValidationAlert
                                        message={t(errors.bankingDetails?.accountOwner?.message ||
                                            "memberForm.errors.unknown-banking-account-owner-error")}
                                    />
                                }

                            </div>
                        </div>

                        <div className="sm:col-span-4">
                            <FieldLabel htmlFor="iban">
                                <Text>{t('memberForm.fields.banking.iban.label')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Controller
                                    name="bankingDetails.iban"
                                    control={control}
                                    render={({field}) => (
                                        <Input
                                            id="iban"
                                            type="text"
                                            autoComplete="iban"
                                            value={formatIBAN(field.value || '')}
                                            onChange={(e) => {
                                                const unformatted = unformatIBAN(e.target.value);
                                                field.onChange(unformatted);
                                            }}
                                            aria-label={t("memberForm.fields.banking.iban.ariaLabel" as any)}
                                            data-invalid={!!errors.bankingDetails?.iban || !!errors.bankingDetails || null}
                                            placeholder={t("memberForm.fields.banking.iban.placeholder" as any)}
                                            data-testid="member-iban"
                                        />
                                    )}
                                />
                                {errors.bankingDetails?.iban &&
                                    <ValidationAlert
                                        message={t(errors.bankingDetails?.iban?.message ||
                                            "memberForm.errors.unknown-banking-iban-error")}
                                    />
                                }

                            </div>
                        </div>

                        <div className="sm:col-span-4">
                            <FieldLabel htmlFor="bic">
                                <Text>{t('memberForm.fields.banking.bic.label')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Input
                                    id="bic"
                                    {...register("bankingDetails.bic")}
                                    type="text"
                                    autoComplete="bic"
                                    aria-label={t("memberForm.fields.banking.bic.ariaLabel" as any)}
                                    data-invalid={!!errors.bankingDetails?.bic || null}
                                    placeholder={t("memberForm.fields.banking.bic.placeholder" as any)}
                                    data-testid="member-bic"
                                />
                                {errors.bankingDetails?.bic &&
                                    <ValidationAlert
                                        message={t(errors.bankingDetails?.bic?.message ||
                                            errors.bankingDetails?.root?.message ||
                                            "memberForm.errors.unknown-banking-bic-error")}
                                    />
                                }

                            </div>
                        </div>

                        <div className="sm:col-span-4">
                            <FieldLabel htmlFor="bank-name">
                                <Text>{t('memberForm.fields.banking.bankName.label')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Input
                                    id="bank-name"
                                    {...register("bankingDetails.bankName")}
                                    type="text"
                                    aria-label={t("memberForm.fields.banking.bankName.ariaLabel" as any)}
                                    data-invalid={!!errors.bankingDetails?.bankName || null}
                                    placeholder={t("memberForm.fields.banking.bankName.placeholder" as any)}
                                />
                                {errors.bankingDetails?.bankName &&
                                    <ValidationAlert
                                        message={t(errors.bankingDetails?.bankName?.message ||
                                            errors.bankingDetails?.root?.message ||
                                            "memberForm.errors.unknown-banking-bank-name-error")}
                                    />
                                }

                            </div>
                        </div>

                        <div className="sm:col-span-4">
                            <FieldLabel htmlFor="mandate-date">
                                <Text>{t('memberForm.fields.banking.mandateDate.label')}</Text>
                            </FieldLabel>
                            <div className="mt-2">

                                <Controller
                                    name="bankingDetails.mandateDate"
                                    control={control}
                                    render={({field}) => (
                                        <Input
                                            id="mandate-date"
                                            type="date"
                                            value={field.value || ''}
                                            onChange={(e) => {
                                                const value =
                                                    e.target.value || null;
                                                field.onChange(value);
                                            }}
                                            aria-label={t("memberForm.fields.banking.mandateDate.label" as any)}
                                            data-invalid={!!errors.bankingDetails?.mandateDate || null}
                                            data-testid="member-mandate-date"
                                        />
                                    )}
                                />

                                {errors.bankingDetails?.mandateDate &&
                                    <ValidationAlert
                                        message={t(errors.bankingDetails?.mandateDate?.message ||
                                            "memberForm.errors.unknown-banking-mandate-date-error")}/>
                                }

                            </div>
                        </div>

                    </div>

                </div>

                {/* Donations Section */}
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div>
                        <Subheading className="text-base/7 font-semibold">
                            {t('memberForm.sections.donations.heading')}
                        </Subheading>
                        <Text className="mt-1 text-sm/6">
                            {t('memberForm.sections.donations.text')}
                        </Text>
                    </div>

                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        {/* One-Time Donation */}
                        <div className="sm:col-span-full">
                            <Text className="font-medium text-sm mb-3">
                                {t('memberForm.sections.donations.oneTime.heading')}
                            </Text>
                        </div>

                        <div className="sm:col-span-3">
                            <FieldLabel htmlFor="one-time-donation-amount">
                                <Text>{t('memberForm.sections.donations.oneTime.amount')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Input
                                    id="one-time-donation-amount"
                                    {...register("oneTimeDonationAmount")}
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={t('memberForm.sections.donations.oneTime.amountPlaceholder')}
                                    data-invalid={!!errors.oneTimeDonationAmount || null}
                                    data-testid="one-time-donation-amount"
                                />
                                {errors.oneTimeDonationAmount && (
                                    <ValidationAlert message={t(errors.oneTimeDonationAmount.message || "memberForm.errors.unknown-error")} />
                                )}
                            </div>
                        </div>

                        <div className="sm:col-span-3">
                            <FieldLabel htmlFor="one-time-donation-date">
                                <Text>{t('memberForm.sections.donations.oneTime.date')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Controller
                                    name="oneTimeDonationDate"
                                    control={control}
                                    render={({field}) => (
                                        <Input
                                            id="one-time-donation-date"
                                            type="date"
                                            value={field.value || ''}
                                            onChange={(e) => field.onChange(e.target.value || null)}
                                            data-testid="one-time-donation-date"
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        {/* Annual Donation */}
                        <div className="sm:col-span-full mt-4">
                            <Text className="font-medium text-sm mb-3">
                                {t('memberForm.sections.donations.annual.heading')}
                            </Text>
                        </div>

                        <div className="sm:col-span-3">
                            <FieldLabel htmlFor="annual-donation-amount">
                                <Text>{t('memberForm.sections.donations.annual.amount')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Input
                                    id="annual-donation-amount"
                                    {...register("annualDonationAmount")}
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={t('memberForm.sections.donations.annual.amountPlaceholder')}
                                    data-invalid={!!errors.annualDonationAmount || null}
                                    data-testid="annual-donation-amount"
                                />
                                {errors.annualDonationAmount && (
                                    <ValidationAlert message={t(errors.annualDonationAmount.message || "memberForm.errors.unknown-error")} />
                                )}
                            </div>
                        </div>

                        <div className="sm:col-span-3">
                            <FieldLabel htmlFor="annual-donation-valid-from">
                                <Text>{t('memberForm.sections.donations.annual.validFrom')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Controller
                                    name="annualDonationValidFrom"
                                    control={control}
                                    render={({field}) => (
                                        <Input
                                            id="annual-donation-valid-from"
                                            type="date"
                                            value={field.value || ''}
                                            onChange={(e) => field.onChange(e.target.value || null)}
                                            data-testid="annual-donation-valid-from"
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div>
                        <Subheading className="text-base/7 font-semibold">
                            {t('memberForm.sections.additionalInfo.heading')}
                        </Subheading>
                        <Text className="mt-1 text-sm/6">
                            {t('memberForm.sections.additionalInfo.text')}
                        </Text>
                    </div>

                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">

                        <div className="sm:col-span-full">
                            <Controller
                                name="fastestMobilityOption"
                                control={control}
                                render={({field: {onChange, value}}) => (
                                    <MobilityOptionCombobox
                                        value={value || null}
                                        onChange={onChange}
                                        options={mobilityOptions}
                                        label={t('memberForm.fields.mobilityOption.label')}
                                        data-testid="mobility-option-combobox"
                                    />
                                )}
                            />
                        </div>

                        <div className="sm:col-span-full">
                            <FieldLabel htmlFor="comments-general">
                                <Text>{t('memberForm.fields.comments.general.label')}</Text>
                            </FieldLabel>
                            <div className="mt-2">
                                <Textarea
                                    id="comments-general"
                                    {...register("generalComments")}
                                    aria-label={t("memberForm.fields.comments.general.label" as any)}
                                    aria-invalid={!!errors.generalComments}
                                    placeholder={t("memberForm.fields.comments.general.placeholder" as any)}
                                    data-testid="member-comments"
                                />
                            </div>
                        </div>

                        {isActiveMember && (
                            <div className="sm:col-span-full">
                                <FieldLabel htmlFor="comments-availability">
                                    <Text>{t('memberForm.fields.comments.availability.label')}</Text>
                                </FieldLabel>
                                <div className="mt-2">
                                    <Textarea
                                        id="comments-availability"
                                        {...register("availabilityComments")}
                                        aria-label={t("memberForm.fields.comments.availability.label" as any)}
                                        aria-invalid={!!errors.availabilityComments}
                                        placeholder={t("memberForm.fields.comments.availability.placeholder" as any)}
                                    />
                                </div>
                            </div>
                        )}

                        {isActiveMember && watchedIsDriver && (
                            <div className="sm:col-span-full">
                                <FieldLabel htmlFor="comments-currentNotes">
                                    <Text>{t('memberForm.fields.comments.currentNotes.label')}</Text>
                                </FieldLabel>
                                <div className="mt-2">
                                    <Textarea
                                        id="comments-currentNotes"
                                        {...register("currentNotes")}
                                        aria-label={t("memberForm.fields.comments.currentNotes.label" as any)}
                                        aria-invalid={!!errors.currentNotes}
                                        placeholder={t("memberForm.fields.comments.currentNotes.placeholder" as any)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

            </div>

            <div className="mt-6 flex items-center justify-end gap-x-6">
                <Button 
                    type="button"
                    plain
                    onClick={() => navigate("/members")}
                    aria-label={t("memberForm.buttons.cancel.ariaLabel" as any)}
                >
                    {t('memberForm.buttons.cancel.title')}
                </Button>
                <Button
                    type="submit"
                    aria-label={t("memberForm.buttons.submit.ariaLabel" as any)}
                    data-testid="save-member-button"
                >
                    {t('memberForm.buttons.submit.title')}
                </Button>
            </div>
        </form>
    )
}
